import React from 'react';

/**
 * FieldRenderer Component
 * 
 * Renders a custom field value based on its type.
 * This is the read-only display - FieldEditor handles editing.
 */
const FieldRenderer = ({ field, fieldType, value, classPrefix = 'mission-card' }) => {
  // Checkbox always shows the checkbox UI (even when unchecked/undefined)
  if (field.type === 'checkbox') {
    const isChecked = value === true;
    return (
      <span className={`${classPrefix}__custom-field-value ${classPrefix}__custom-field-value--checkbox`}>
        <span className={`${classPrefix}__checkbox ${isChecked ? `${classPrefix}__checkbox--checked` : ''}`}>
          {isChecked && (
            <svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="2,6 5,9 10,3" />
            </svg>
          )}
        </span>
      </span>
    );
  }

  // Empty state for other field types
  if (value === undefined || value === null || value === '') {
    return (
      <span className={`${classPrefix}__custom-field-value ${classPrefix}__custom-field-value--empty`}>
        —
      </span>
    );
  }

  // Render based on field type
  switch (field.type) {
    case 'text':
      return (
        <span className={`${classPrefix}__custom-field-value`} title={value}>
          {value}
        </span>
      );

    case 'number':
      return (
        <span className={`${classPrefix}__custom-field-value ${classPrefix}__custom-field-value--number`}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </span>
      );

    case 'select':
      const selectedOption = field.options?.find(opt => opt.id === value);
      if (!selectedOption) {
        return (
          <span className={`${classPrefix}__custom-field-value ${classPrefix}__custom-field-value--empty`}>
            —
          </span>
        );
      }
      return (
        <span 
          className={`${classPrefix}__custom-field-value ${classPrefix}__custom-field-value--select`}
          style={{ 
            '--option-color': selectedOption.color,
            backgroundColor: `${selectedOption.color}20`,
            color: selectedOption.color,
          }}
        >
          {selectedOption.label}
        </span>
      );

    case 'date':
      // Format date nicely
      const date = new Date(value);
      const isValidDate = !isNaN(date.getTime());
      if (!isValidDate) {
        return (
          <span className={`${classPrefix}__custom-field-value ${classPrefix}__custom-field-value--empty`}>
            —
          </span>
        );
      }
      // Show relative date for recent dates
      const today = new Date();
      const diffDays = Math.floor((date - today) / (1000 * 60 * 60 * 24));
      let dateDisplay;
      if (diffDays === 0) {
        dateDisplay = 'Today';
      } else if (diffDays === 1) {
        dateDisplay = 'Tomorrow';
      } else if (diffDays === -1) {
        dateDisplay = 'Yesterday';
      } else {
        dateDisplay = date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      }
      return (
        <span className={`${classPrefix}__custom-field-value ${classPrefix}__custom-field-value--date`}>
          {dateDisplay}
        </span>
      );

    case 'url':
      return (
        <a 
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className={`${classPrefix}__custom-field-value ${classPrefix}__custom-field-value--url`}
          onClick={(e) => e.stopPropagation()}
          title={value}
        >
          {(() => {
            try {
              const url = new URL(value);
              return url.hostname.replace('www.', '');
            } catch {
              return 'Link';
            }
          })()}
        </a>
      );

    default:
      return (
        <span className={`${classPrefix}__custom-field-value`}>
          {String(value)}
        </span>
      );
  }
};

export default FieldRenderer;
